import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { JjsvBanner } from '../../components/jjsv-banner/jjsv-banner.component';
import { JjsvNotification } from '../../components/jjsv-notification/jjsv-notification.component';
import { JjsvTile } from '../../components/jjsv-tile/jjsv-tile.component';
import { JjsvTable, TableRow } from '../../components/jjsv-table/jjsv-table.component';
import { JjsvLoader } from '../../components/jjsv-loader/jjsv-loader.component';
import { getCurrentLanguage } from '../../core/i18n/translation-locale';
import { homePageTranslationsEn } from '../../../assets/translations/en/homePage';
import { homePageTranslationsFr } from '../../../assets/translations/fr/homePage';
import { PagesService } from '../pages.service';
import { NotificationMessage } from '../pages.models';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { formatDateTimeForDisplay } from '../../core/utils/date-format.util';

@Component({
  selector: 'app-home-page',
  imports: [CommonModule, JjsvBanner, JjsvNotification, JjsvTile, JjsvTable, JjsvLoader],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
})

export class HomePage implements OnInit, OnDestroy {

  private readonly destroyRef = inject(DestroyRef);
  private readonly language = getCurrentLanguage();
  readonly translations = this.language === 'fr' ? homePageTranslationsFr : homePageTranslationsEn;
  HomeComponentData$: Observable<any>;
  baseUrl: string = environment.baseUrl;
  notificationMessage$: Observable<NotificationMessage | null>;

  constructor(
    private pagesService: PagesService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.HomeComponentData$ = this.pagesService.getHomeComponentData().pipe(
      takeUntilDestroyed(this.destroyRef)
    );

    this.notificationMessage$ = this.pagesService.message$.pipe(
      takeUntilDestroyed(this.destroyRef)
    );
  }

  navLinks = [
    { label: this.translations.homeNavLabel, path: '/homepage', isActive: true },
    { label: this.translations.purchaseOrdersNavLabel, path: '/purchase-orders', isActive: false },
  ];

  navContent = {
    link: this.navLinks,
    loginStatus: true,
    subNavLinks: [],
  };

  tableColumns = [
    { key: 'requestDate', header: this.translations.tableRequestDateHeader, width: '12%' },
    { key: 'shipTo', header: this.translations.tableShipToHeader, width: '20%' },
    { key: 'poNumber', header: this.translations.tablePoNumberHeader, width: '8%' },
    { key: 'beingReviewedBy', header: this.translations.tableValidatedByHeader, width: '12%' },
    { key: 'comment', header: this.translations.tableCommentHeader, width: '30%' },
    { key: 'status', header: this.translations.tableStatusHeader, width: '18%' },
  ];

  tableRows: TableRow[] = [];
  readonly isLoading = signal<boolean>(false);

  ngOnInit() {
    this.pagesService.updateNavigationNodes(this.navContent);
    this.loadTableRows();
  }

  ngOnDestroy(): void {
    // Redirect banner is one-shot: clear it so it doesn't reappear on a later Home visit.
    this.pagesService.clear();
  }

  private loadTableRows(): void {
    this.isLoading.set(true);
    this.pagesService.getTableData().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (rows: any) => {
        this.tableRows = rows?.response || [];
        this.isLoading.set(false);
        this.cdr.detectChanges(); 
      },
      error: () => {
        this.tableRows = [];
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
    });
  }

  /** Display-only copy of the rows with timeZone-based dates; memoized so change detection keeps a stable reference. */
  getDisplayRows(timeZone?: string | null): TableRow[] {
    if (this.displaySource !== this.tableRows || this.displayZone !== (timeZone ?? '')) {
      this.displaySource = this.tableRows;
      this.displayZone = timeZone ?? '';
      this.displayRows = this.tableRows.map((row) => ({
        ...row,
        requestDate: formatDateTimeForDisplay(typeof row['requestDate'] === 'string' ? row['requestDate'] : '', timeZone),
      }));
    }
    return this.displayRows;
  }

  private displaySource: TableRow[] | null = null;
  private displayZone = '';
  private displayRows: TableRow[] = [];

  onTileClick(title: string): void {
    this.pagesService.setPurchaseOrderTitle(title);
    this.router.navigate(['/purchase-orders']);
  }

  refreshTableData(): void {
    this.loadTableRows();
    this.HomeComponentData$ = this.pagesService.refreshHomeComponentData().pipe(
      takeUntilDestroyed(this.destroyRef)
    );
    this.cdr.detectChanges();
  }
}
